import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningIcon from '@mui/icons-material/Warning';
import { GridActionsCellItemProps, GridRenderCellParams, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { FakeButtonLink, GridActionsCellItemLink } from '../../components/FakeLink';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { usePermissions, useSearchParamsState } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { cleanOrderExecutions, fetchOrderExecutions } from '../../redux/modules/orderExecution/orderExecution.actions';
import { orderExecutionByRequestIdSelector } from '../../redux/modules/orderExecution/orderExecution.selectors';
import { ReducerState } from '../../redux/store.types';
import { locations } from '../../routes/locations';
import { PortfolioOrderExecutionStatus } from '../../types/portfolioOrder';
import { parseJSON } from '../../utils/json';
import * as permissions from '../../utils/permissions';

const orderExecutionStatusToInfo: {
  [prop in PortfolioOrderExecutionStatus]: {
    color: 'error' | 'info' | 'success' | 'warning';
    icon: React.ElementType;
  };
} = {
  [PortfolioOrderExecutionStatus.EXECUTION]: {
    color: 'info',
    icon: InfoIcon,
  },
  [PortfolioOrderExecutionStatus.APPROVED]: {
    color: 'success',
    icon: CheckCircleIcon,
  },
  [PortfolioOrderExecutionStatus.IN_REVIEW]: {
    color: 'warning',
    icon: WarningIcon,
  },
  [PortfolioOrderExecutionStatus.RECONCILIATION]: {
    color: 'warning',
    icon: WarningIcon,
  },
};

export function OrderExecutionList() {
  const requestId = 'order-executions-list-all';
  const dispatch = useAppDispatch();
  const tableRef = useRef<TableRef>(null);
  const canRead = usePermissions({ items: [permissions.OrderExecution.read] });
  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'Id',
      width: 80,
    },
    {
      field: 'executionId',
      headerName: 'Execution Id',
      type: 'string',
      width: 300,
    },
    {
      field: 'orderId',
      headerName: 'OrderId',
      type: 'string',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }

        return (
          <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.portfolioOrder(params.value)}>
            {params.value}
          </FakeButtonLink>
        );
      },
    },
    {
      field: 'status.name',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(PortfolioOrderExecutionStatus),
      minWidth: 150,
      valueGetter: (params) => params.row?.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const info = orderExecutionStatusToInfo[params.row?.status?.name as PortfolioOrderExecutionStatus] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return params.row?.status?.name;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={params.row?.status?.name} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);
  const { totalOrderExecutions, orderExecutions, fetchStatus } = useSelector((state: ReducerState) =>
    orderExecutionByRequestIdSelector(state, requestId),
  );
  const loading = fetchStatus === fetchStatuses.pending;
  const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', '');
  const [initialPage, setInitialPage] = useSearchParamsState('page', '0');
  const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', '50');

  useEffect(
    () => () => {
      dispatch(cleanOrderExecutions());
    },
    [],
  );

  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (canRead && `${params.id}`.split('/').length === 2) {
          const executionId = `${params.id}`.split('/').pop();

          actions.push(
            <GridActionsCellItemLink
              key={`order-execution-list-read-${executionId}`}
              icon={<OpenInNewIcon />}
              label="Link"
              href={locations.orderExecution(executionId)}
            />,
          );
        }

        return actions;
      },
    };

    if (canRead) {
      setColumns([actionColumn, ...columns]);
    }
  }, [canRead]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">Order Executions &nbsp;</Typography>
      </Box>
      <Table
        ref={tableRef}
        fetchItems={fetchOrderExecutions}
        rows={orderExecutions}
        columns={columns}
        loading={loading}
        rowCount={totalOrderExecutions}
        initialFilterModel={parseJSON(initialFilterModel)}
        initialPage={+initialPage}
        initialPageSize={+initialPageSize}
        fetchItemsCustomParams={{ requestId }}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        initialColumnVisibilityModel={{ id: false, executionId: false }}
        onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        onPageChange={(page) => setInitialPage(`${page}`)}
        onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
        disableRowGrouping={false}
        rowGroupingColumnMode="single"
        initialRowGroupingModel={['executionId']}
        defaultGroupingExpansionDepth={1}
      />
    </Container>
  );
}
